
// Enhanced feedback detection for both JSON and legacy text formats
export const isEnhancedFeedbackAnalysis = (text: string): boolean => {
  if (!text || typeof text !== 'string') {
    console.log('🎯 ENHANCED-FEEDBACK: Invalid text input');
    return false;
  }

  console.log('🎯 ENHANCED-FEEDBACK: Testing content length:', text.length);
  console.log('🎯 ENHANCED-FEEDBACK: Content preview:', text.substring(0, 200));

  // First check if it's JSON feedback format
  if (isJSONFeedbackFormat(text)) {
    console.log('✅ ENHANCED-FEEDBACK: CONFIRMED - JSON feedback format detected');
    return true;
  }

  // Legacy detection for plain text feedback (backwards compatibility)
  const legacyMarkers = [
    '## 📊', '## ✅', '## ❌', '## 🔄', '## 💡',
    '### 📊', '### ✅', '### ❌', '### 🔄', '### 💡',
    'Target Location:', 'User\'s Impressions and Analysis:',
    '✅ Matches:', '❌ Mismatches:', '✅ Match:', '❌ Mismatch:',
    'Analysis:', 'Overall,', 'User\'s Submitted Image:',
    'Written Impressions:', 'Submitted Image:'
  ];

  const hasLegacyMarker = legacyMarkers.some(marker => {
    const found = text.includes(marker);
    if (found) {
      console.log('🎯 ENHANCED-FEEDBACK: Found legacy marker:', marker);
    }
    return found;
  });

  if (hasLegacyMarker) {
    console.log('✅ ENHANCED-FEEDBACK: CONFIRMED - Legacy feedback format detected');
    return true;
  }

  console.log('❌ ENHANCED-FEEDBACK: NOT DETECTED - No feedback format recognized');
  return false;
};

// Check if content is JSON feedback format
export const isJSONFeedbackFormat = (text: string): boolean => {
  // First try to extract JSON from markdown code blocks
  const jsonMatch = text.match(/```json\s*([\s\S]*?)\s*```/);
  let jsonText = jsonMatch ? jsonMatch[1] : text.trim();
  
  try {
    const parsed = JSON.parse(jsonText);
    // Support both regular feedback and prediction feedback formats
    const validTypes = ['feedback_analysis', 'prediction_feedback_analysis'];
    return validTypes.includes(parsed.type) && 
           parsed.target_overview && 
           parsed.analysis;
  } catch {
    return false;
  }
};

// Parse JSON feedback or return null if not valid JSON feedback
export const parseJSONFeedback = (text: string): any | null => {
  // First try to extract JSON from markdown code blocks
  const jsonMatch = text.match(/```json\s*([\s\S]*?)\s*```/);
  let jsonText = jsonMatch ? jsonMatch[1] : text.trim();
  
  try {
    const parsed = JSON.parse(jsonText);
    // Support both regular feedback and prediction feedback formats
    if (parsed.type === 'feedback_analysis' || parsed.type === 'prediction_feedback_analysis') {
      console.log('🎯 JSON-FEEDBACK: Successfully parsed JSON feedback, type:', parsed.type);
      return parsed;
    }
  } catch (error) {
    console.log('🎯 JSON-FEEDBACK: Failed to parse JSON:', error);
  }
  return null;
};

// NEW: Feedback preprocessor to convert plain text feedback into proper markdown
export const preprocessFeedbackForMarkdown = (content: string): string => {
  console.log('🔄 FEEDBACK-PREPROCESSOR: Starting preprocessing');
  
  let processed = content;
  
  // Convert feedback sections to proper markdown headers
  processed = processed.replace(/Target Location:/g, '## 🎯 Target Location');
  processed = processed.replace(/User's Impressions and Analysis:/g, '## 📝 User\'s Impressions and Analysis');
  processed = processed.replace(/Analysis:/g, '### 📊 Analysis');
  processed = processed.replace(/✅ Matches?:/g, '### ✅ Accurate Elements');
  processed = processed.replace(/❌ Mismatches?:/g, '### ❌ Inaccurate Elements');
  processed = processed.replace(/Overall,/g, '### 💡 Overall Assessment\n\n');
  
  // Fix bullet points and line breaks
  processed = processed.replace(/- /g, '\n- ');
  processed = processed.replace(/(\d+\.\s)/g, '\n$1');
  
  // Ensure proper spacing around sections
  processed = processed.replace(/###/g, '\n\n###');
  processed = processed.replace(/##/g, '\n\n##');
  
  // Clean up excessive newlines
  processed = processed.replace(/\n{4,}/g, '\n\n\n');
  processed = processed.trim();
  
  console.log('🔄 FEEDBACK-PREPROCESSOR: Preprocessing complete');
  console.log('🔄 FEEDBACK-PREPROCESSOR: Processed preview:', processed.substring(0, 300));
  
  return processed;
};

// Validation function to ensure content is ready for markdown rendering
export const validateMarkdownContent = (content: string): { isValid: boolean; issues: string[] } => {
  const issues: string[] = [];
  
  // Check for common markdown corruption indicators
  if (content.includes('\\n') && !content.includes('\n')) {
    issues.push('Escaped newlines detected - may need processing');
  }
  
  if (content.includes('###') && !content.match(/^###\s/m)) {
    issues.push('Malformed heading syntax detected');
  }
  
  if (content.includes('**') && (content.match(/\*\*/g) || []).length % 2 !== 0) {
    issues.push('Unmatched bold markdown syntax');
  }
  
  // Check for line break issues
  const lines = content.split('\n');
  if (lines.length < 5 && content.length > 500) {
    issues.push('Possible line break loss - content too long with too few breaks');
  }
  
  return {
    isValid: issues.length === 0,
    issues
  };
};

// Convert JSON feedback to markdown format for copying
export const convertJSONFeedbackToMarkdown = (feedback: any): string => {
  const { target_overview, user_impressions, analysis, overall_assessment } = feedback;
  
  // Handle prediction feedback analysis
  if (feedback.type === 'prediction_feedback_analysis') {
    let markdown = `# 🔮 Prediction Target Analysis

## Option A (YES)
${target_overview.optionA_description}

## Option B (NO)
${target_overview.optionB_description}`;

    if (user_impressions?.written_impressions) {
      markdown += `

## 📝 Your Impressions
${user_impressions.written_impressions}`;
    }

    markdown += `

## 📊 Analysis`;

    if (analysis.optionA_matches?.length > 0) {
      markdown += `
### Option A ✅ Matches
${analysis.optionA_matches.map((item: string) => `- ${item}`).join('\n')}`;
    }
    if (analysis.optionB_matches?.length > 0) {
      markdown += `
### Option B ✅ Matches
${analysis.optionB_matches.map((item: string) => `- ${item}`).join('\n')}`;
    }

    if (analysis.stronger_alignment) {
      markdown += `

## 🎯 Alignment: ${analysis.stronger_alignment === 'neither' ? 'Neither' : `Option ${analysis.stronger_alignment}`}
${analysis.alignment_reasoning || ''}`;
    }

    markdown += `

## ⚖️ Overall Assessment
${overall_assessment}`;
    return markdown;
  }
  
  // Regular feedback
  let markdown = `# 🎯 Target Analysis

## Target Overview
**Type:** ${target_overview.type}  
**Target:** ${target_overview.description}`;

  if (target_overview.surrounding_area) {
    markdown += `\n**Surrounding Area:** ${target_overview.surrounding_area}`;
  }

  markdown += `\n\n## 📝 Your Impressions`;
  if (user_impressions?.written_impressions) {
    markdown += `\n${user_impressions.written_impressions}`;
  }

  markdown += `\n\n## 📊 Analysis`;
  if (analysis.matches?.length > 0) {
    markdown += `\n### ✅ Matches\n${analysis.matches.map((item: string) => `- ${item.replace(/^✅\s*/, '')}`).join('\n')}`;
  }
  if (analysis.mismatches?.length > 0) {
    markdown += `\n### ❌ Mismatches\n${analysis.mismatches.map((item: string) => `- ${item.replace(/^❌\s*/, '')}`).join('\n')}`;
  }
  if (analysis.partial_connections?.length > 0) {
    markdown += `\n### 🔄 Partial Connections\n${analysis.partial_connections.map((item: string) => `- ${item.replace(/^🔄\s*/, '')}`).join('\n')}`;
  }
  if (analysis.additional_observations?.length > 0) {
    markdown += `\n### 💡 Additional Observations\n${analysis.additional_observations.map((item: string) => `- ${item.replace(/^💡\s*/, '')}`).join('\n')}`;
  }

  markdown += `\n\n## ⚖️ Overall Assessment\n${overall_assessment}`;
  return markdown;
};
